import { Navigate } from "react-router";
import type { AppRouteObject } from "./types";
import { lazyRoute } from "./lazyRoute";
import { withInstitutionRootMetadata } from "./featureRouteMetadata";

export const institutionRoutes = withInstitutionRootMetadata({
    path: "institutions",
    handle: { help: null },
    children: [
        {
            index: true,
            lazy: lazyRoute(() => import("../containers/Institutions/Institutions/InstitutionsPage")),
            handle: { help: { topicKey: "institutions" } }
        },
        {
            path: "create",
            lazy: lazyRoute(() => import("../containers/Institutions/Institution/InstitutionPageSWR"), { mode: "create" }),
            handle: { help: { topicKey: "institution" } }
        },
        {
            path: ":institutionId",
            handle: { help: null },
            children: [
                {
                    index: true,
                    element: <Navigate replace to="details" />,
                    handle: { help: null }
                },
                {
                    path: "details",
                    lazy: lazyRoute(() => import("../containers/Institutions/Institution/InstitutionPageSWR"), { mode: "edit" }),
                    handle: { help: { topicKey: "institution" } }
                },
                {
                    path: "institutionitems",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionItems/InstitutionItemsPageSWR")),
                            handle: { help: { topicKey: "institution-items" } }
                        },
                    ],
                },
                {
                    path: "personnel",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionPersonnels/InstitutionPersonnelsPage")),
                            handle: { help: { topicKey: "institution-personnels" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionPersonnel/InstitutionPersonnelPage")),
                            handle: { help: { topicKey: "institution-personnel" } }
                        },
                        {
                            path: ":trialPersonnelId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Institutions/InstitutionPersonnel/InstitutionPersonnelPage")),
                                    handle: { help: { topicKey: "institution-personnel" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "labs",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionLaboratories/InstitutionLaboratoriesPage")),
                            handle: { help: { topicKey: "institution-laboratories" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionLaboratory/InstitutionLaboratoryPage"), { mode: "create" }),
                            handle: { help: { topicKey: "institution-laboratory" } }
                        },
                        {
                            path: ":institutionLaboratoryId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Institutions/InstitutionLaboratory/InstitutionLaboratoryPage"), { mode: "edit" }),
                                    handle: { help: { topicKey: "institution-laboratory" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "notes",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionNotes/InstitutionNotesPage")),
                            handle: { help: { topicKey: "institution-notes" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Notes/Note/NotePageSWR"), { kind: "institution", mode: "create" }),
                            handle: { help: { topicKey: "note" } }
                        },
                        {
                            path: ":noteId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Notes/Note/NotePageSWR"), { kind: "institution", mode: "edit" }),
                                    handle: { help: { topicKey: "note" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "procedures",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionProcedures/InstitutionProceduresPage")),
                            handle: { help: { topicKey: "institution-procedures" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionProcedure/InstitutionProcedurePage")),
                            handle: { help: { topicKey: "institution-procedure" } }
                        },
                        {
                            path: ":institutionProcedureId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Institutions/InstitutionProcedure/InstitutionProcedurePage")),
                                    handle: { help: { topicKey: "institution-procedure" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "finance",
                    lazy: lazyRoute(() => import("../containers/Institutions/InstitutionFinance/InstitutionFinancePage")),
                    handle: { help: { topicKey: "institution-finance" } }
                },
                {
                    path: "contract",
                    lazy: lazyRoute(() => import("../containers/Institutions/InstitutionContract/InstitutionContractPage")),
                    handle: { help: { topicKey: "institution-contract" } }
                },
                {
                    path: "codes",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionCodes/InstitutionCodesPage")),
                            handle: { help: { topicKey: "institution-codes" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Institutions/InstitutionCode/InstitutionCodePage"), { mode: "create" }),
                            handle: { help: { topicKey: "institution-code" } }
                        },
                        {
                            path: ":institutionCodeId",
                            handle: { help: { topicKey: "dashboard" } },
                            children: [
                                {
                                    index: true,
                                    element: <Navigate replace to="details" />,
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Institutions/InstitutionCode/InstitutionCodePage"), { mode: "edit" }),
                                    handle: { help: { topicKey: "institution-code" } }
                                }
                            ],
                        },
                    ]
                },
                {
                    path: "trials",
                    lazy: lazyRoute(() => import("../containers/Institutions/InstitutionTrials/InstitutionTrialsPage")),
                    handle: { help: { topicKey: "institution-trials" } }
                }
            ],
        },
    ],
} satisfies AppRouteObject);

